import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import {
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import { useUpdateFolderMutation } from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";

const DIALOG_ID = "rename-folder-dialog";

// Validation constants
const MIN_FOLDER_NAME_LENGTH = 1;
const MAX_FOLDER_NAME_LENGTH = 100;

interface IProps {
  folder: IStoriesFolder;
}

export const RenameFolderDialog = ({ folder }: IProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(folder.name);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: updateFolder, isPending: isLoading } =
    useUpdateFolderMutation();

  // Auto-focus and select text on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Memoized validation
  const validation = useMemo(() => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return { isValid: false, error: "" };
    }
    if (trimmedName.length < MIN_FOLDER_NAME_LENGTH) {
      return {
        isValid: false,
        error: "Folder name must be at least 1 character",
      };
    }
    if (trimmedName.length > MAX_FOLDER_NAME_LENGTH) {
      return {
        isValid: false,
        error: `Folder name must be less than ${MAX_FOLDER_NAME_LENGTH} characters`,
      };
    }
    if (trimmedName === folder.name) {
      return {
        isValid: false,
        error: "Please enter a different name",
      };
    }
    return { isValid: true, error: "" };
  }, [name, folder.name]);

  // Memoized form data
  const formData = useMemo(() => {
    if (!validation.isValid) return null;

    return {
      id: folder._id,
      name: name.trim(),
    };
  }, [folder._id, name, validation.isValid]);

  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (!formData || isLoading) return;

    setError("");

    try {
      await updateFolder(formData);
      track("folder renamed successfully", {
        folderId: folder._id,
        oldName: folder.name,
        newName: formData.name,
      });
      toast.success("Folder renamed successfully!");
      handleClose();
    } catch (e: any) {
      const errorMessage =
        e?.data?.message || e?.message || "Error updating folder name";
      setError(errorMessage);

      track("error updating stories folder name", {
        folder,
        newFolderName: formData.name,
        error: e,
        errorMessage,
      });

      toast.error(errorMessage);
    }
  }, [formData, isLoading, updateFolder, folder, handleClose]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === "Enter" && validation.isValid) {
        handleSubmit();
      }
    },
    [handleSubmit, validation.isValid],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
      // Clear error when user starts typing
      if (error) setError("");
    },
    [error],
  );

  return (
    <Card className="tw-w-[468px]">
      <CardHeader className="tw-flex tw-items-center tw-justify-between">
        <CardTitle>Rename folder</CardTitle>
        <CardCloseButton onClick={handleClose} />
      </CardHeader>
      <CardContent>
        <div className="tw-space-y-2">
          <Input
            ref={inputRef}
            placeholder="Add your folder name here..."
            value={name}
            onChange={handleNameChange}
            onKeyDown={handleKeyDown}
            className={cn(
              "tw-h-9 tw-rounded-lg tw-px-3 tw-font-sans tw-text-base tw-font-normal",
              error && "tw-focus:tw-border-red-500 tw-border-red-500",
            )}
            aria-invalid={!!error}
            aria-describedby={error ? "folder-name-error" : undefined}
            maxLength={MAX_FOLDER_NAME_LENGTH}
          />

          {error && (
            <p
              id="folder-name-error"
              className="tw-font-sans tw-text-sm tw-text-red-600"
              role="alert"
            >
              {error}
            </p>
          )}

          {name.length > MAX_FOLDER_NAME_LENGTH * 0.8 && (
            <p className="tw-text-gray-500 tw-font-sans tw-text-xs">
              {name.length}/{MAX_FOLDER_NAME_LENGTH} characters
            </p>
          )}
        </div>
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-4">
        <Button
          variant="secondary"
          className="tw-font-sans tw-text-sm tw-font-medium"
          onClick={handleClose}
          disabled={isLoading}
        >
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!validation.isValid || isLoading}
          className={cn(
            "tw-font-sans tw-text-sm tw-font-medium",
            isLoading && "tw-cursor-not-allowed",
          )}
          aria-describedby={error ? "folder-name-error" : undefined}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

RenameFolderDialog.id = DIALOG_ID;
